import React, { useEffect, useRef, useCallback, useState, useContext } from 'react';
import { City } from '../types/trip';
import { validatePlaceId } from '../utils/inputValidation';
import { GoogleMapsContext } from '../App';

interface CountrySearchProps {
  onSelectCountry: (country: City | null) => void;
  className?: string;
  placeholder?: string;
  searchType?: 'cities' | 'countries';
}

const CountrySearch: React.FC<CountrySearchProps> = ({ 
  onSelectCountry, 
  className = '', 
  placeholder = 'Select a Country',
  searchType = 'countries'
}) => {
  const isGoogleMapsLoaded = useContext(GoogleMapsContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isSelected, setIsSelected] = useState(false);

  const updateInputValue = useCallback((value: string) => {
    if (inputRef.current) {
      inputRef.current.value = value;
    }
    setIsSelected(true);
  }, []);

  const handlePlaceChanged = useCallback(() => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    console.log('🌍 Place selected:', place);

    if (!place || !place.place_id) {
      console.error('❌ Invalid place selected');
      return;
    }

    try {
      // Validate the place ID
      validatePlaceId(place.place_id);

      // Extract country information
      const addressComponents = place.address_components || [];
      
      let countryComponent;
      if (searchType === 'countries') {
        countryComponent = addressComponents.find(
          comp => comp.types.includes('country')
        );
      } else {
        // For cities, still get the country component
        countryComponent = addressComponents.find(
          comp => comp.types.includes('country')
        );
      }

      if (!countryComponent && searchType === 'countries') {
        console.error('❌ No country component found in place');
        return;
      }

      const name = place.name || countryComponent?.long_name || 'Unknown';
      const countryName = countryComponent?.long_name || 'Unknown Country';

      const selectedPlace: City = {
        id: place.place_id,
        name: name,
        country: countryName,
        coordinates: {
          lat: place.geometry?.location?.lat() || 0,
          lng: place.geometry?.location?.lng() || 0
        }
      };

      console.log('✅ Created selectedPlace object:', selectedPlace);
      
      // Update component state
      updateInputValue(name);
      
      // Pass the selection back to parent component
      onSelectCountry(selectedPlace);
    } catch (error) {
      console.error('Invalid place selection:', error);
      return;
    }
  }, [onSelectCountry, searchType, updateInputValue]);

  useEffect(() => {
    if (!inputRef.current || !isGoogleMapsLoaded) return;
    
    // Check if Google Maps is loaded before initializing
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps not yet loaded, will retry in 2 seconds');
      const retryTimer = setTimeout(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          console.log('Google Maps now loaded, initializing autocomplete');
          // Re-trigger the effect by calling the initialization logic
          initializeAutocomplete();
        }
      }, 2000);
      return () => clearTimeout(retryTimer);
    }
    
    initializeAutocomplete();
  }, [handlePlaceChanged, searchType, isGoogleMapsLoaded]);

  const initializeAutocomplete = () => {
    if (!inputRef.current || !window.google || !window.google.maps || !window.google.maps.places) {
      console.log('❌ Cannot initialize autocomplete - missing dependencies:', {
        inputRef: !!inputRef.current,
        google: !!window.google,
        maps: !!(window.google && window.google.maps),
        places: !!(window.google && window.google.maps && window.google.maps.places)
      });
      return;
    }
    
    console.log('🔧 Setting up traditional Google Maps Autocomplete');
    
    // Set up the traditional Google Maps Autocomplete
    const options: google.maps.places.AutocompleteOptions = {
      types: searchType === 'countries' ? ['country'] : ['(cities)'],
      fields: ['place_id', 'name', 'address_components', 'geometry', 'photos', 'formatted_address']
    };

    try {
      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
      
      // Add event listener for place selection
      autocompleteRef.current.addListener('place_changed', handlePlaceChanged);
      
      console.log('✅ Traditional autocomplete initialized successfully', {
        autocomplete: !!autocompleteRef.current,
        inputElement: !!inputRef.current,
        options: options
      });
    } catch (error) {
      console.error('❌ Error initializing autocomplete:', error);
    }
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clean up event listeners
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  return (
    <div className="relative w-full">
      <style>{`
        .country-search-input {
          font-family: 'Buenos Aires', sans-serif !important;
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
        .country-search-input::placeholder {
          color: #6b7280 !important;  /* text-gray-500 to match date inputs */
          opacity: 1 !important;
          font-family: 'Buenos Aires', sans-serif !important;
        }
        .country-search-input:focus {
          border-color: #d1d5db !important;
          box-shadow: none !important;
          outline: none !important;
        }
      `}</style>
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        className={`country-search-input w-full py-2 px-4 text-base border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 transition-all duration-200 text-black focus:outline-none box-border ${className}`}
        autoComplete="off"
      />
      {!isSelected && (
        <div className="text-xs text-red-500 mt-1" style={{ display: 'none' }}>
          Please select an option from the dropdown list
        </div>
      )}
    </div>
  );
};

export default CountrySearch;